import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertTriangle, Download, FileUp, X } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import type { SellerTagType } from '@/lib/types/feature/seller-tag';
import {
  buildImportPlan,
  buildWideImportPlan,
  isWideFormat,
  parseWideImportText,
  parseImportText,
  type DuplicateResolution,
  type ImportPlan,
  type ImportSeller,
} from './lib/buildImportPlan';
import { strictKey } from './lib/tagNormalize';

/**
 * CSV/TSV ファイルをテキストとして読む。
 *
 * 雛形は UTF-8（BOM付き）で出しているが、Excel の「CSV（コンマ区切り）」で保存し直すと
 * Windows では Shift_JIS になる。UTF-8 として読めなければ Shift_JIS で読み直す。
 */
async function readTextFile(file: File): Promise<string> {
  // File.arrayBuffer() ではなく FileReader を使う（jsdom に arrayBuffer が無く、テストできないため）
  const buffer = await new Promise<ArrayBuffer>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as ArrayBuffer);
    reader.onerror = () => reject(reader.error ?? new Error('ファイルを読み込めませんでした'));
    reader.readAsArrayBuffer(file);
  });

  try {
    // BOM は TextDecoder が取り除く
    return new TextDecoder('utf-8', { fatal: true }).decode(buffer);
  } catch {
    return new TextDecoder('shift_jis').decode(buffer);
  }
}

interface TagImportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tagType: SellerTagType;
  sellers: ImportSeller[];
  /** 選択中のタグ種別の現在の付与（店舗ID → タグ名ID） */
  currentAssignments: Map<string, string>;
  /** 全タグ種別。ワイド形式でヘッダの種別名を引き当てるのに使う */
  allTagTypes: SellerTagType[];
  /** タグ種別ID → (店舗の内部ID → タグID)。ワイド形式の差分計算に使う */
  assignmentsByType: Map<string, Map<string, string>>;
  submitting: boolean;
  /** 全タグ種別ぶんの雛形（ワイド形式）を落とす */
  onDownloadTemplate: () => void;
  /**
   * 登録を確定する。
   * @param entries 店舗IDとタグ名の組。タグ名IDの解決は呼び出し側で行う
   * @param replaceMode true なら、CSVに無い店舗のタグを外す（洗い替え）
   */
  onSubmit: (entries: ImportPlan['entries'], replaceMode: boolean) => void;
  /**
   * 複数タグ種別をまとめて登録する（ワイド形式）。
   * @param createMissingTypes true なら、CSVにしか無いタグ種別も作る
   */
  onSubmitWide: (
    plan: ReturnType<typeof buildWideImportPlan>,
    replaceMode: boolean,
    createMissingTypes: boolean,
  ) => void;
}

/**
 * タグの一括登録（FKV3-13002）
 *
 * 100店舗規模のタグ付けを1回で終わらせるための主動線。雛形CSVを落として Excel で
 * タグ名の列を埋め、そのまま貼り戻す。店舗IDを手で打つ場面が無いので打ち間違いが起きない。
 *
 * 確定前に「何が起きるか」を必ず見せる。特に、同じ店舗が複数行にあるケースは
 * 1店舗1タグ名の制約からどちらを採るか決められないので、明示的に選ばせる。
 */
export function TagImportDialog({
  open,
  onOpenChange,
  tagType,
  sellers,
  currentAssignments,
  allTagTypes,
  assignmentsByType,
  submitting,
  onDownloadTemplate,
  onSubmit,
  onSubmitWide,
}: TagImportDialogProps) {
  const [step, setStep] = useState<1 | 2>(1);
  // ファイルと貼り付けはどちらか一方しか使わないので、タブで切り替える
  const [inputMode, setInputMode] = useState<'file' | 'paste'>('file');
  const [text, setText] = useState('');
  const [duplicateResolution, setDuplicateResolution] = useState<DuplicateResolution>('last');
  const [replaceMode, setReplaceMode] = useState(false);
  // CSVにしか無いタグ種別を作るかどうか。誤記で種別が増えないよう既定はオフ
  const [createMissingTypes, setCreateMissingTypes] = useState(false);
  const [mergeInto, setMergeInto] = useState<Map<string, string>>(new Map());
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) {
      setStep(1);
      setInputMode('file');
      setText('');
      setDuplicateResolution('last');
      setReplaceMode(false);
      setCreateMissingTypes(false);
      setMergeInto(new Map());
      setFileName(null);
      setFileError(null);
      setDragging(false);
    }
  }, [open]);

  const loadFile = async (file: File) => {
    setFileError(null);
    try {
      const content = await readTextFile(file);
      setText(content);
      setFileName(file.name);
    } catch {
      setFileName(null);
      setFileError(`${file.name} を読み込めませんでした。CSV / TSV ファイルを指定してください`);
    }
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files[0];
    if (file) void loadFile(file);
  };

  const clearFile = () => {
    setFileName(null);
    setFileError(null);
    setText('');
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  // 「タグ名」列が無ければ、タグ種別が列に並ぶワイド形式として扱う
  const wide = useMemo(() => isWideFormat(text), [text]);
  const parsed = useMemo(() => parseImportText(text), [text]);
  const parsedWide = useMemo(
    () =>
      wide
        ? parseWideImportText(
            text,
            allTagTypes.map((t) => ({
              id: t.id,
              name: t.name,
              tags: t.tags.map((v) => ({ id: v.id, name: v.name })),
            })),
          )
        : { columns: [] },
    [wide, text, allTagTypes],
  );
  // 判定した形式の側のエラーだけ出す。ワイド形式を1種別形式として読んだエラーは意味を持たない
  const parseError = wide ? parsedWide.error : parsed.error;
  const widePlan = useMemo(
    () =>
      buildWideImportPlan({
        columns: parsedWide.columns,
        sellers,
        existingTypes: allTagTypes.map((t) => ({
          id: t.id,
          name: t.name,
          tags: t.tags.map((v) => ({ id: v.id, name: v.name })),
        })),
        assignmentsByType,
        duplicateResolution,
      }),
    [parsedWide.columns, sellers, allTagTypes, assignmentsByType, duplicateResolution],
  );

  const buildPlan = useCallback(
    (skipNewTagNames: boolean) =>
      buildImportPlan({
        rows: parsed.rows,
        sellers,
        existingTags: tagType.tags.map((v) => ({ id: v.id, name: v.name })),
        currentAssignments,
        duplicateResolution,
        skipNewTagNames,
        mergeInto,
      }),
    [parsed.rows, sellers, tagType.tags, currentAssignments, duplicateResolution, mergeInto],
  );

  const plan = useMemo(() => buildPlan(false), [buildPlan]);

  const toggleMerge = (name: string, similarTo: string | undefined, merge: boolean) => {
    const next = new Map(mergeInto);
    if (merge && similarTo) next.set(strictKey(name), similarTo);
    else next.delete(strictKey(name));
    setMergeInto(next);
  };

  const handleSubmit = (skipNewTagNames: boolean) => {
    if (wide) {
      onSubmitWide(widePlan, replaceMode, createMissingTypes);
      return;
    }
    const target = skipNewTagNames ? buildPlan(true) : plan;
    onSubmit(target.entries, replaceMode);
  };

  /** ワイド形式で、種別を作らない設定なのに未知の種別がある場合は進ませない */
  const blockedByMissingTypes = wide && !createMissingTypes && widePlan.newTagTypeNames.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-3xl">
        <DialogHeader>
          <DialogTitle>タグの一括登録</DialogTitle>
          <DialogDescription>
            {wide ? `${parsedWide.columns.length}種別をまとめて登録` : `種別: ${tagType.name}`} ／{' '}
            {step === 1 ? '1 貼り付け' : '2 確認'} → {step === 1 ? '2 確認' : '3 反映'}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="flex flex-col gap-4">
            <div className="flex flex-wrap items-center gap-3">
              <Button type="button" onClick={onDownloadTemplate}>
                <Download className="mr-1 h-4 w-4" />
                雛形CSVをダウンロード
              </Button>
              <span className="text-xs text-gray-500">{sellers.length}店舗 × 全タグ種別</span>
              <span className="w-full text-xs text-gray-500">
                店舗階層抽出タブのCSV（タグ種別が列に並ぶ形式）も、そのまま貼り戻せます
              </span>
            </div>

            <Tabs
              value={inputMode}
              onValueChange={(value) => setInputMode(value as 'file' | 'paste')}
            >
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="file">ファイルから</TabsTrigger>
                <TabsTrigger value="paste">貼り付け</TabsTrigger>
              </TabsList>

              <TabsContent value="file" className="flex flex-col gap-3">
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                  className={`flex flex-col items-center gap-2 rounded-md border-2 border-dashed px-4 py-5 text-center transition ${
                    dragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
                  }`}
                >
                  <FileUp className="h-5 w-5 text-gray-400" />
                  <div className="text-xs text-gray-600">
                    編集したファイルをここにドロップ、または
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      className="h-auto px-1 py-0 text-xs"
                      onClick={() => fileInputRef.current?.click()}
                    >
                      ファイルを選ぶ
                    </Button>
                  </div>
                  <span className="text-[11px] text-gray-500">
                    CSV / TSV に対応。Excel が Shift_JIS で保存したファイルも読めます
                  </span>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,.tsv,.txt,text/csv,text/plain"
                    className="hidden"
                    aria-label="登録するファイルを選ぶ"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) void loadFile(file);
                    }}
                  />
                </div>

                {fileName && (
                  <div
                    role="status"
                    className="flex items-center gap-2 rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800"
                  >
                    <span className="flex-1 truncate">
                      <b>{fileName}</b> を読み込みました（
                      {wide
                        ? `${parsedWide.columns.length}種別 × ${parsedWide.columns[0]?.rows.length ?? 0}行`
                        : `${parsed.rows.length}行`}
                      ）
                    </span>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-6 px-2"
                      onClick={clearFile}
                    >
                      <X className="mr-1 h-3 w-3" />
                      取り消す
                    </Button>
                  </div>
                )}

                {fileError && (
                  <p
                    role="alert"
                    className="rounded-md border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                  >
                    {fileError}
                  </p>
                )}
              </TabsContent>

              <TabsContent value="paste" className="flex flex-col gap-1.5">
                <Label htmlFor="tag-import-text">
                  {fileName ? '読み込んだ内容（ここで直せます）' : 'CSVの内容'}
                </Label>
                <Textarea
                  id="tag-import-text"
                  rows={8}
                  value={text}
                  onChange={(e) => {
                    setText(e.target.value);
                    // 手で直した時点でファイルの内容とは別物になる
                    if (fileName) setFileName(null);
                  }}
                  placeholder={
                    '店舗ID,階層,店舗名,タグ名\nS001,全社/関西/大阪,梅田,Honeys分析100店'
                  }
                  className="font-mono text-xs"
                />
                <span className="text-xs text-gray-500">
                  階層・店舗名は目で確かめるための列で、登録には使いません（店舗IDとタグ名だけを見ます）。区切り文字・BOM・ヘッダ行は自動で判定します。
                </span>
                {parseError && text.trim().length > 0 && (
                  <span className="text-xs text-red-600">{parseError}</span>
                )}
              </TabsContent>
            </Tabs>
          </div>
        ) : wide ? (
          <div className="flex flex-col gap-3">
            {widePlan.newTagTypeNames.length > 0 && (
              <section
                className={`rounded-md border ${
                  createMissingTypes ? 'border-amber-200' : 'border-red-300'
                }`}
              >
                <h3
                  className={`flex items-center gap-1.5 border-b px-3 py-2 text-xs font-medium ${
                    createMissingTypes
                      ? 'border-amber-200 bg-amber-50 text-amber-900'
                      : 'border-red-300 bg-red-50 text-red-800'
                  }`}
                >
                  <AlertTriangle className="h-3.5 w-3.5" />
                  まだ無いタグ種別が {widePlan.newTagTypeNames.length}件 あります
                </h3>
                <div className="px-3 py-2 text-xs">
                  <p className="text-gray-700">{widePlan.newTagTypeNames.join(' ／ ')}</p>
                  <label className="mt-2 flex items-center gap-1.5">
                    <Checkbox
                      checked={createMissingTypes}
                      onCheckedChange={(checked) => setCreateMissingTypes(checked === true)}
                    />
                    <span>これらのタグ種別も新しく作る</span>
                  </label>
                  {!createMissingTypes && (
                    <p className="mt-1 text-red-700">
                      作らない場合、これらの列は登録されません。列名の打ち間違いでないか確認してください
                    </p>
                  )}
                </div>
              </section>
            )}

            <section className="rounded-md border">
              <h3 className="border-b bg-gray-50 px-3 py-2 text-xs font-medium">
                タグ種別ごとの登録内容 {widePlan.entries.length}種別
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs tabular-nums">
                  <thead className="bg-gray-50 text-gray-600">
                    <tr>
                      <th className="px-3 py-1.5 text-left font-medium">タグ種別</th>
                      <th className="px-3 py-1.5 text-left font-medium">新しく作るタグ名</th>
                      <th className="px-3 py-1.5 text-right font-medium">新しく付与</th>
                      <th className="px-3 py-1.5 text-right font-medium">置き換え</th>
                      <th className="px-3 py-1.5 text-right font-medium">変更なし</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {widePlan.entries.map((entry) => {
                      const skipped = !entry.tagTypeId && !createMissingTypes;
                      return (
                        <tr key={entry.tagTypeName} className={skipped ? 'text-gray-400' : ''}>
                          <td className="px-3 py-1.5">
                            {entry.tagTypeName}
                            {!entry.tagTypeId && (
                              <span className="ml-1.5 rounded bg-amber-50 px-1.5 py-0.5 text-[11px] text-amber-800">
                                新規
                              </span>
                            )}
                            {skipped && <span className="ml-1.5 text-[11px]">登録しません</span>}
                          </td>
                          <td className="px-3 py-1.5 text-left">
                            {entry.plan.newTagNames.length > 0
                              ? entry.plan.newTagNames.map((t) => t.name).join('、')
                              : '—'}
                          </td>
                          <td className="px-3 py-1.5 text-right text-green-700">
                            +{entry.plan.newlyAssigned}
                          </td>
                          <td className="px-3 py-1.5 text-right text-amber-700">
                            {entry.plan.replaced}
                          </td>
                          <td className="px-3 py-1.5 text-right">{entry.plan.unchanged}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <p className="border-t px-3 py-2 text-xs text-gray-500">
                空欄の列は「指定なし」として扱い、既存のタグはそのまま残します。
              </p>
            </section>

            {widePlan.entries.some((e) => e.plan.unknownSellerIds.length > 0) && (
              <section className="rounded-md border">
                <h3 className="border-b bg-gray-50 px-3 py-2 text-xs font-medium">
                  マスタに存在しない店舗ID（登録されません）
                </h3>
                <div className="px-3 py-2 text-xs text-gray-600">
                  {Array.from(
                    new Set(
                      widePlan.entries.flatMap((e) =>
                        e.plan.unknownSellerIds.map((u) => u.sellerCustomerId),
                      ),
                    ),
                  )
                    .slice(0, 20)
                    .join(' ／ ')}
                </div>
              </section>
            )}

            <label className="flex items-start gap-2 rounded-md border px-3 py-2.5 text-sm">
              <Checkbox
                className="mt-0.5"
                checked={replaceMode}
                onCheckedChange={(checked) => setReplaceMode(checked === true)}
              />
              <span>
                CSVに無い店舗のタグを外す（洗い替え）
                <span className="mt-0.5 block text-xs text-gray-500">
                  CSVに書かれた全タグ種別が対象です。空欄の店舗はそのタグ種別のタグが外れます
                </span>
                {replaceMode && (
                  <span className="mt-1 block text-xs text-amber-700">
                    外れる店舗:{' '}
                    {widePlan.entries
                      .filter((e) => e.tagTypeId)
                      .map((e) => `${e.tagTypeName} ${e.plan.unassignedIfReplace}件`)
                      .join(' ／ ')}
                  </span>
                )}
              </span>
            </label>
          </div>
        ) : (
          <div className="flex flex-col gap-3">
            {plan.newTagNames.length > 0 && (
              <section className="rounded-md border">
                <h3 className="border-b bg-gray-50 px-3 py-2 text-xs font-medium">
                  新しく作られるタグ名 {plan.newTagNames.length}件
                </h3>
                <div className="divide-y">
                  {plan.newTagNames.map((newName) => (
                    <div
                      key={newName.name}
                      className="flex flex-wrap items-center gap-2 px-3 py-2 text-xs"
                    >
                      <span className="font-medium">{newName.name}</span>
                      {newName.similarTo ? (
                        <>
                          <span className="rounded bg-amber-50 px-2 py-0.5 text-amber-800">
                            「{newName.similarTo}」に似ています
                          </span>
                          <label className="flex items-center gap-1.5">
                            <Checkbox
                              checked={mergeInto.has(strictKey(newName.name))}
                              onCheckedChange={(checked) =>
                                toggleMerge(newName.name, newName.similarTo, checked === true)
                              }
                            />
                            <span>「{newName.similarTo}」にまとめる</span>
                          </label>
                        </>
                      ) : (
                        <span className="text-gray-500">新規</span>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {plan.duplicateSellers.length > 0 && (
              <section className="rounded-md border border-amber-200">
                <h3 className="flex items-center gap-1.5 border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  同じ店舗に2つ以上のタグ名が指定されています {plan.duplicateSellers.length}件
                </h3>
                <div className="divide-y">
                  {plan.duplicateSellers.map((dup) => (
                    <div key={dup.sellerCustomerId} className="px-3 py-2 text-xs">
                      <span className="tabular-nums text-gray-500">{dup.sellerCustomerId}</span>{' '}
                      {dup.sellerName}
                      <span className="ml-2 text-gray-500">
                        {dup.rows.map((r) => `${r.lineNumber}行目「${r.tagName}」`).join(' ／ ')}
                      </span>
                    </div>
                  ))}
                </div>
                <div className="flex flex-wrap items-center gap-3 border-t border-amber-200 px-3 py-2 text-xs">
                  <span className="text-gray-600">
                    1店舗に付くのは1つだけです。どちらを採りますか？
                  </span>
                  {(
                    [
                      ['last', '後の行を採用'],
                      ['first', '先の行を採用'],
                      ['skip', 'これらの店舗をスキップ'],
                    ] as const
                  ).map(([value, label]) => (
                    <label key={value} className="flex items-center gap-1.5">
                      <input
                        type="radio"
                        name="duplicate-resolution"
                        checked={duplicateResolution === value}
                        onChange={() => setDuplicateResolution(value)}
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </section>
            )}

            {plan.unknownSellerIds.length > 0 && (
              <section className="rounded-md border">
                <h3 className="border-b bg-gray-50 px-3 py-2 text-xs font-medium">
                  マスタに存在しない店舗ID {plan.unknownSellerIds.length}件（登録されません）
                </h3>
                <div className="px-3 py-2 text-xs text-gray-600">
                  {plan.unknownSellerIds
                    .slice(0, 20)
                    .map((u) => `${u.lineNumber}行目: ${u.sellerCustomerId}`)
                    .join(' ／ ')}
                  {plan.unknownSellerIds.length > 20 &&
                    ` ほか${plan.unknownSellerIds.length - 20}件`}
                </div>
              </section>
            )}

            <section className="rounded-md border px-3 py-2.5">
              <div className="flex flex-wrap gap-5 text-sm tabular-nums">
                <span>
                  <span className="mr-1.5 text-xs text-gray-500">新しく付与</span>
                  <b className="text-green-700">+{plan.newlyAssigned}</b>
                </span>
                <span>
                  <span className="mr-1.5 text-xs text-gray-500">別の値に置き換え</span>
                  <b className="text-amber-700">{plan.replaced}</b>
                </span>
                <span>
                  <span className="mr-1.5 text-xs text-gray-500">変更なし</span>
                  {plan.unchanged}
                </span>
              </div>
              <p className="mt-1.5 text-xs text-gray-500">
                1店舗に付くのは1つだけなので、すでに別のタグ名が付いている店舗は置き換えになります。
              </p>
            </section>

            <label className="flex items-start gap-2 text-xs">
              <Checkbox
                checked={replaceMode}
                onCheckedChange={(checked) => setReplaceMode(checked === true)}
                className="mt-0.5"
              />
              <span className="text-gray-700">
                貼り付けたデータに無い店舗の「{tagType.name}」タグを外す（この種別を洗い替える）
                {replaceMode && plan.unassignedIfReplace > 0 && (
                  <b className="ml-1 text-red-600">
                    — {plan.unassignedIfReplace}店舗のタグが外れます
                  </b>
                )}
              </span>
            </label>
          </div>
        )}

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            className="mr-auto"
            onClick={() => onOpenChange(false)}
          >
            キャンセル
          </Button>
          {step === 1 ? (
            <Button
              type="button"
              disabled={wide ? parsedWide.columns.length === 0 : parsed.rows.length === 0}
              onClick={() => setStep(2)}
            >
              内容を確認する
            </Button>
          ) : (
            <>
              <Button type="button" variant="outline" onClick={() => setStep(1)}>
                戻る
              </Button>
              {!wide && plan.newTagNames.length > 0 && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={submitting}
                  onClick={() => handleSubmit(true)}
                >
                  既存のタグ名だけ登録する
                </Button>
              )}
              <Button
                type="button"
                disabled={submitting || blockedByMissingTypes}
                onClick={() => handleSubmit(false)}
              >
                {wide
                  ? `${widePlan.entries.filter((e) => e.tagTypeId || createMissingTypes).length}種別を登録する`
                  : plan.newTagNames.length > 0
                    ? '新規作成して登録する'
                    : '登録する'}
              </Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
